import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { ManualWorkflowViewEnum } from '@app/core/model/entities/workflow/workflow';
import { Work } from '@app/core/model/entities/works/work';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { DocumentModalService } from '@app/features/main/views/organization-documents/modals/document-modal.service';
import { WorkModalService } from '@app/features/main/views/works/works-inventory/modals/work-modal.service';
import { WorksService } from '@app/features/main/views/works/works.service';
import { TranslateService } from '@ngx-translate/core';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'work-header',
  templateUrl: './work-header.component.html',
  styleUrls: ['./work-header.component.scss'],
  providers: [WorkModalService, DocumentModalService],
  standalone: false
})
export class WorkHeaderComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  protected readonly accessManager = inject(AccessManager);
  private readonly activationEndService = inject(ActivationEndService);
  protected readonly appManager = inject(AppManager);
  private readonly titleService = inject(Title);
  private readonly worksService = inject(WorksService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly snackbarManager = inject(SnackbarManager);
  private readonly router = inject(Router);
  private readonly workModalService = inject(WorkModalService);

  public work: Work;
  public Permission = PermissionEnum;
  protected readonly ManualWorkflowViewEnum = ManualWorkflowViewEnum;
  protected readonly EntityType = EntityTypeEnum;

  private destroyRef = inject(DestroyRef);

  /**
   * Get current Work entity, initialize form fields and set page title.
   */
  public ngOnInit(): void {
    this.work = this.appManager.currentEntity as Work;

    // Update Work as it is updated outside the header
    this.worksService.workUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(updatedWork => {
        this.work = updatedWork;
      });

    this.activationEndService.pushOptionalParams({
      'TITLE.WORK_IDENTIFIER': {
        identifier: this.work.identifier.toString()
      }
    });
    this.titleService.setTitle(`${this.translate.instant(
      'TITLE.WORK_IDENTIFIER',
      {identifier: this.work.identifier}
    )} - myA`);
  }

  /**
   * Open the 'delete Work' dialog then delete the current Work entity if the user confirms.
   */
  public deleteWork(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SHEET,
      NavigateToEnum.DELETE_DIALOG,
      EntityTypeEnum.WORK,
      this.work.id
    );
    this.workModalService.openDeleteWorksDialog([this.work])
      .pipe(
        filter(result => !!result),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.WORK_DELETED'));
        this.router.navigate(['organization', this.appManager.currentOrganization.id, 'works'], {replaceUrl: true})
          .catch(console.error);
      });
  }

  /**
   * Redirects the user to the Asset sheet attached to the current Work
   */
  public showAssetSheet(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SHEET,
      NavigateToEnum.SHEET,
      EntityTypeEnum.ASSET,
      this.work.assetId
    );

    this.router.navigate([
      'organization',
      this.appManager.currentOrganization.id,
      'assets',
      'asset-sheet',
      this.work.assetId,
      'works'
    ]).catch(console.error);
  }
}
